import { mix } from '../icons/pix';
import { type Ctx, dot, drop, fistHand, M, SCENES, type Scene, spark, speedLines, sword } from './scenes';

/** Beasts, bosses and the odd props: pictures for moves nothing else covers. */
const extra: Record<string, Scene> = {
  batFang: (L, x) => {
    L.fillPoly([[2, 6], [10, 11], [12, 9], [14, 11], [22, 6], [19, 15], [15, 13], [12, 18], [9, 13], [5, 15]], x.t.d);
    L.fillPoly([[4, 8], [10, 12], [12, 11], [14, 12], [20, 8], [18, 13], [12, 15], [6, 13]], x.t.c);
    L.rect(10, 13, 1, 3, M.bone);
    L.rect(13, 13, 1, 3, M.bone);
    L.set(9, 10, M.red);
    L.set(15, 10, M.red);
  },
  spiderFang: (L, x) => {
    L.line(12, 1, 12, 7, M.cloth, 1);
    L.disc(12, 10, 4, x.t.d);
    L.disc(12, 10, 3, x.t.c);
    L.curve(8, 11, 6, 18, 9, 20, M.bone, 2);
    L.curve(16, 11, 18, 18, 15, 20, M.bone, 2);
    L.set(10, 9, M.red);
    L.set(14, 9, M.red);
    drop(L, 12, 18, M.poison, '#dfffb0');
  },
  centipede: (L, x) => {
    for (let i = 0; i < 6; i++) {
      const px = 3 + i * 3;
      const py = 18 - i * 2 + (i % 2);
      L.disc(px, py, 2, i % 2 ? x.t.c : x.t.l);
      L.line(px - 2, py - 3, px - 2, py + 3, M.boneDark, 1);
      L.line(px + 2, py - 3, px + 2, py + 3, M.boneDark, 1);
    }
    L.curve(19, 5, 22, 4, 21, 1, M.bone, 2);
    L.curve(17, 3, 14, 2, 15, 0, M.bone, 2);
    drop(L, 19, 12, M.poison, '#dfffb0');
  },
  snakeHead: (L, x) => {
    L.curve(3, 21, 3, 8, 10, 8, x.t.c, 4);
    L.fillPoly([[9, 4], [20, 6], [21, 10], [9, 12]], x.t.c);
    L.fillPoly([[10, 5], [19, 7], [19, 9], [10, 9]], x.t.l);
    L.set(14, 7, M.red);
    L.rect(19, 10, 1, 4, M.bone);
    L.line(21, 12, 23, 14, M.red, 1);
  },
  foxTail: (L, x) => {
    L.curve(5, 21, 0, 8, 12, 5, x.t.c, 5);
    L.curve(12, 5, 20, 3, 20, 12, x.t.c, 5);
    L.curve(12, 5, 20, 3, 20, 12, x.t.hi, 1);
    L.disc(20, 12, 2, '#ffffff');
    spark(L, 4, 4, 1, x.t.hi);
    spark(L, 20, 20, 1, x.t.hi);
  },
  tigerFace: (L, x) => {
    L.disc(12, 12, 8, '#f0a030');
    for (const o of [-5, -2, 2, 5]) L.line(12 + o, 4, 12 + o, 7, M.ink, 1);
    L.line(4, 10, 7, 11, M.ink, 1);
    L.line(20, 10, 17, 11, M.ink, 1);
    L.rect(8, 10, 2, 2, M.yellow);
    L.rect(14, 10, 2, 2, M.yellow);
    L.rect(11, 13, 2, 2, M.red);
    L.rect(9, 16, 6, 3, M.cloth);
    L.rect(9, 18, 1, 2, M.bone);
    L.rect(14, 18, 1, 2, M.bone);
    void x;
  },
  swordRain: (L, x) => {
    for (const [px, py] of [[5, 2], [10, 5], [15, 1], [19, 6], [8, 11], [14, 10]] as const) {
      L.line(px, py, px - 1, py + 7, M.steel, 1);
      L.rect(px - 2, py, 3, 1, M.gold);
    }
    L.line(2, 21, 21, 21, x.t.d);
    spark(L, 18, 16, 1, x.t.hi);
  },
  saltJar: (L, x) => {
    L.rect(7, 6, 10, 2, M.wood);
    L.rect(6, 8, 12, 12, M.cloth);
    L.rect(6, 8, 2, 12, M.steelMid);
    L.rect(9, 11, 6, 6, x.t.l);
    L.rect(11, 12, 2, 4, M.red);
    spark(L, 19, 5, 1, x.t.hi);
    dot(L, 4, 4, x.t.hi);
  },
  dragonChain: (L, x) => {
    SCENES.chain!(L, x);
    L.fillPoly([[16, 1], [22, 2], [22, 7], [18, 8], [15, 5]], M.gold);
    L.set(19, 3, M.red);
    L.line(15, 1, 14, 0, M.goldDark, 1);
  },
  sharkJaw: (L, x) => {
    L.rect(3, 5, 18, 4, x.t.d);
    L.rect(3, 15, 18, 4, x.t.d);
    for (let i = 0; i < 5; i++) {
      L.fillPoly([[4 + i * 3.4, 9], [6.8 + i * 3.4, 9], [5.4 + i * 3.4, 13]], M.cloth);
      L.fillPoly([[5.5 + i * 3.4, 15], [8.3 + i * 3.4, 15], [6.9 + i * 3.4, 11]], M.bone);
    }
    spark(L, 20, 3, 1, x.t.hi);
  },
  eggs: (L, x) => {
    for (const [px, py] of [[7, 15], [13, 16], [11, 8]] as const) {
      L.fillPoly([[px, py - 4], [px + 2, py - 2], [px + 3, py + 1], [px + 1, py + 3], [px - 1, py + 3], [px - 3, py + 1], [px - 2, py - 2]], M.cloth);
      L.set(px - 1, py - 1, '#ffffff');
      L.rect(px - 1, py + 1, 3, 1, M.boneDark);
    }
    spark(L, 19, 5, 1, x.t.hi);
  },
  birdFan: (L, x) => {
    for (const [a, c] of [[-0.9, x.t.d], [-0.45, x.t.c], [0, x.t.l], [0.45, x.t.c]] as const) {
      L.line(11, 21, 11 + Math.sin(a) * 13, 21 - Math.cos(a) * 17, c, 3);
    }
    L.rect(9, 20, 5, 2, M.gold);
  },
  skyBurn: (L, x) => {
    L.disc(12, 12, 7, M.fire);
    L.disc(12, 12, 4, M.yellow);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * 6.283;
      L.line(12 + Math.cos(a) * 8, 12 + Math.sin(a) * 8, 12 + Math.cos(a) * 11, 12 + Math.sin(a) * 11, i % 2 ? M.fire : M.yellow, 1);
    }
    L.arc(12, 12, 10, 0.5, 2.5, x.t.hi);
  },
  apeChest: (L, x) => {
    fistHand(L, 0, 8);
    fistHand(L, 11, 8);
    L.arc(12, 12, 10, -2.2, -0.9, x.t.hi);
    burst2(L, x);
  },
  turtleBolts: (L, x) => {
    SCENES.shell!(L, x);
    L.poly([[19, 1], [15, 6], [19, 7], [14, 12]], M.yellow, 2);
    L.poly([[5, 1], [3, 5]], M.yellow, 1);
  },
  turtleTide: (L, x) => {
    SCENES.shell!(L, x);
    L.curve(2, 6, 8, 0, 14, 5, x.t.hi, 2);
    L.curve(10, 4, 16, -1, 22, 5, '#7fd8ff', 2);
  },
  turtleThousand: (L, x) => {
    for (const [px, py, r] of [[7, 7, 4], [16, 8, 4], [11, 16, 5], [19, 18, 3], [4, 17, 3]] as const) {
      L.disc(px, py, r, x.t.c);
      L.line(px - r + 1, py, px + r - 1, py, x.t.d, 1);
      L.set(px - 1, py - r + 1, x.t.hi);
    }
  },
  turtleRam: (L, x) => {
    speedLines(L, [[8, 8, 6], [7, 13, 5], [8, 18, 6]], x.t.l);
    SCENES.shell!(L, x);
  },
  turtleFlipper: (L, x) => {
    L.fillPoly([[3, 18], [8, 8], [15, 4], [21, 5], [17, 11], [11, 16], [6, 21]], x.t.c);
    L.line(8, 14, 16, 7, x.t.d, 1);
    L.line(11, 17, 18, 9, x.t.d, 1);
    L.line(5, 20, 12, 12, x.t.hi, 1);
    speedLines(L, [[7, 6, 4], [20, 18, 4]], x.t.l);
  },
  ringFeather: (L, x) => {
    SCENES.ring!(L, x);
    L.curve(5, 21, 8, 10, 19, 6, M.cloth, 2);
  },
  sunBird: (L, x) => {
    L.disc(12, 12, 5, M.fire);
    L.disc(12, 12, 3, M.yellow);
    L.fillPoly([[3, 6], [10, 11], [3, 14]], x.t.c);
    L.fillPoly([[21, 6], [14, 11], [21, 14]], x.t.c);
    L.fillPoly([[10, 16], [14, 16], [12, 22]], x.t.l);
  },
  mimic: (L, x) => {
    L.disc(8, 12, 5, x.t.c);
    L.disc(16, 12, 5, x.t.l);
    L.rect(6, 10, 2, 2, M.ink);
    L.rect(14, 10, 2, 2, M.ink);
    L.arc(12, 12, 10, 0, 6.3, x.t.hi, 1);
    spark(L, 20, 4, 1, x.t.hi);
  },
  treeSwing: (L, x) => {
    L.line(4, 21, 14, 4, M.wood, 4);
    L.line(5, 21, 15, 4, M.woodDark, 1);
    L.fillPoly([[14, 2], [20, 3], [21, 8], [16, 9]], M.jade);
    L.arc(12, 14, 9, -0.4, 1.3, x.t.hi);
    drop(L, 20, 18, x.t.l, x.t.hi);
  },
  leap: (L, x) => {
    L.arc(12, 18, 10, -2.9, -0.25, x.t.l, 2);
    L.disc(6, 8, 2, M.skin);
    L.rect(5, 10, 3, 5, x.t.d);
    L.line(1, 20, 22, 20, x.t.d);
    burst2(L, x);
  },
};

function burst2(L: Parameters<Scene>[0], x: Ctx): void {
  spark(L, 12, 12, 1, x.t.hi);
  void mix;
  void sword;
}

Object.assign(SCENES, extra);
